/* Display region — the one visitor choice that drives BOTH language and
   currency. Until Colombia, currency simply tracked language (es ⇒ MXN);
   Colombia breaks that 1:1 (Spanish UI, COP prices), so the region is now
   its own stored value:

     US ⇒ English + USD      MX ⇒ Spanish + MXN      CO ⇒ Spanish + COP

   Resolution order:
     1. the country the user picked in the switcher (localStorage)
     2. the geolocated country from useGeoLocale (localStorage geo_country)
     3. the language as a last resort (es ⇒ MX — the pre-Colombia behavior)

   Components subscribe through useDisplayRegion()/useDisplayCurrency(); a
   custom window event keeps every subscriber in sync when the user picks. */

import { useSyncExternalStore } from "react";
import i18n from "i18next";

export type Region = "US" | "MX" | "CO";

const STORAGE_REGION = "display_region";
const STORAGE_COUNTRY = "geo_country"; // written by useGeoLocale
const EVENT = "display-region-changed";

// Labels are the ISO 3166-1 alpha-3 country codes (USA/MEX/COL — not the
// currency codes) so every row stays on one line in the dropdown.
export const REGIONS: Array<{ code: Region; flag: string; label: string; language: "en" | "es" }> = [
  { code: "US", flag: "🇺🇸", label: "USA", language: "en" },
  { code: "MX", flag: "🇲🇽", label: "MEX", language: "es" },
  { code: "CO", flag: "🇨🇴", label: "COL", language: "es" },
];

const isRegion = (v: string | null): v is Region => v === "US" || v === "MX" || v === "CO";

export function resolveRegion(): Region {
  try {
    const picked = localStorage.getItem(STORAGE_REGION);
    if (isRegion(picked)) return picked;
    const geo = localStorage.getItem(STORAGE_COUNTRY);
    if (isRegion(geo)) return geo;
  } catch { /* private mode */ }
  return i18n.language?.startsWith("es") ? "MX" : "US";
}

/** Store the visitor's region choice and notify every subscriber. */
export function setRegion(region: Region) {
  try { localStorage.setItem(STORAGE_REGION, region); } catch { /* private mode */ }
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(cb: () => void) {
  window.addEventListener(EVENT, cb);
  window.addEventListener("storage", cb);
  i18n.on("languageChanged", cb);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener("storage", cb);
    i18n.off("languageChanged", cb);
  };
}

/** Reactive region for components (re-renders on switcher picks and language changes). */
export function useDisplayRegion(): Region {
  return useSyncExternalStore(subscribe, resolveRegion, () => "US" as Region);
}
